// ===== 全屏登录门 =====
// 账号密码登录 → 服务端验证（scrypt）→ 领取 Bearer token。
// 服务器不可达时给出明确提示（登录必须经服务器，无离线通道）。

import { useState, type FormEvent } from 'react';
import { Lock, User, Loader2, CloudOff, Eye, EyeOff } from 'lucide-react';
import { login, type AuthApiError } from '@/lib/authApi';
import { useAuth } from '@/contexts/AuthContext';
import {
  ICP_BEIAN, POLICE_BEIAN, ICP_BEIAN_URL, POLICE_BEIAN_URL, shouldShowBeian,
} from '@/lib/siteBeian';

export function LoginGate() {
  const { onLoggedIn } = useAuth();
  // 备案号仅在已备案的正式域名下展示（香港旧站不显示，避免备案号与域名不符）
  const showBeian = shouldShowBeian();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [serverDown, setServerDown] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    if (!username.trim() || !password) { setError('请输入账号和密码'); return; }
    setBusy(true);
    setError('');
    setServerDown(false);
    try {
      const s = await login(username.trim(), password);
      await onLoggedIn(s);
    } catch (err) {
      const ae = err as AuthApiError;
      if (ae.status === 0) setServerDown(true);
      setError(ae.message || '登录失败，请重试');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 pb-20"
      style={{ background: 'linear-gradient(160deg, #f4f6f5 0%, #e8efec 60%, #dce9e4 100%)' }}>
      <div className="w-full max-w-[380px]">
        {/* 品牌 */}
        <div className="flex flex-col items-center mb-7">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-white text-[22px] font-bold shadow-lg shadow-emerald-900/20"
            style={{ background: 'linear-gradient(135deg, #059669, #047857)' }}>
            昌
          </div>
          <div className="mt-3 text-[19px] font-semibold text-zinc-900">昌发工作台</div>
          <div className="text-[12.5px] text-zinc-500 mt-0.5">多账号云端同步 · 数据实时一致</div>
        </div>

        {/* 表单卡片 */}
        <form onSubmit={submit} className="bg-white/90 backdrop-blur rounded-2xl shadow-xl shadow-zinc-900/5 border border-zinc-200/80 p-6">
          <div className="space-y-3.5">
            <div>
              <label className="text-[12px] text-zinc-500 mb-1.5 block">账号</label>
              <div className="relative">
                <User className="w-4 h-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  autoComplete="username"
                  placeholder="请输入账号"
                  className="w-full h-11 pl-9 pr-3 rounded-xl border border-zinc-200 bg-white text-[14px] outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/15 transition"
                />
              </div>
            </div>
            <div>
              <label className="text-[12px] text-zinc-500 mb-1.5 block">密码</label>
              <div className="relative">
                <Lock className="w-4 h-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type={showPw ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  placeholder="请输入密码"
                  className="w-full h-11 pl-9 pr-10 rounded-xl border border-zinc-200 bg-white text-[14px] outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/15 transition"
                />
                <button type="button" onClick={() => setShowPw((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600">
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>

          {error && (
            <div className="mt-3 text-[12.5px] text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2 leading-snug">
              {serverDown && <CloudOff className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />}
              {error}
              {serverDown && <span className="block mt-0.5 text-red-400">请检查网络后重试；本机已有数据不受影响。</span>}
            </div>
          )}

          <button type="submit" disabled={busy}
            className="mt-5 w-full h-11 rounded-xl text-white text-[14.5px] font-medium flex items-center justify-center gap-2 disabled:opacity-60 transition active:scale-[0.99]"
            style={{ background: 'linear-gradient(135deg, #059669, #047857)' }}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
            {busy ? '登录中…' : '登 录'}
          </button>
        </form>

        <div className="text-center text-[11.5px] text-zinc-400 mt-5 leading-relaxed">
          账号由老板在「系统管理 → 用户管理」中创建<br />
          忘记密码请联系管理员重置
        </div>
      </div>

      {/* 备案号：固定在页面底部（工信部要求首页底部展示，且必须可点击跳转备案查询系统） */}
      {showBeian && (
        <div className="fixed bottom-0 inset-x-0 pb-3 flex flex-col items-center gap-1 text-[11.5px] text-zinc-500">
          {ICP_BEIAN && (
            <a href={ICP_BEIAN_URL} target="_blank" rel="noreferrer"
              className="hover:text-zinc-700 transition-colors">
              {ICP_BEIAN}
            </a>
          )}
          {POLICE_BEIAN && (
            <a href={POLICE_BEIAN_URL} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1 hover:text-zinc-700 transition-colors">
              {/* ⚠️ 填公安备案号之前，先把官方徽标图片放到 public/gongan-beian.png */}
              <img src="/gongan-beian.png" alt="" className="w-3.5 h-3.5" />
              {POLICE_BEIAN}
            </a>
          )}
        </div>
      )}
    </div>
  );
}
